import { createTheme, ThemeOptions } from '@mui/material/styles';

const lightThemeOptions: ThemeOptions = {
  palette: {
    mode: 'light',
    primary: { 
      main: '#1976d2',
      light: '#42a5f5',
      dark: '#1565c0',
      contrastText: '#ffffff'
    },
    secondary: { 
      main: '#7c3aed',
      light: '#8b5cf6',
      dark: '#6d28d9',
      contrastText: '#ffffff'
    },
    success: { 
      main: '#2e7d32',
      light: '#4caf50',
      dark: '#1b5e20',
      contrastText: '#ffffff'
    },
    warning: { 
      main: '#ed6c02',
      light: '#ff9800',
      dark: '#e65100',
      contrastText: '#ffffff'
    },
    error: { 
      main: '#d32f2f',
      light: '#ef5350',
      dark: '#c62828',
      contrastText: '#ffffff'
    },
    info: { 
      main: '#0288d1',
      light: '#03a9f4',
      dark: '#01579b',
      contrastText: '#ffffff'
    },
    background: { 
      default: '#f7f8fa', 
      paper: '#ffffff' 
    },
    text: {
      primary: '#1f2937',
      secondary: '#6b7280'
    },
    divider: '#e5e7eb'
  }
};

const darkThemeOptions: ThemeOptions = {
  palette: {
    mode: 'dark',
    primary: { 
      main: '#42a5f5',
      light: '#64b5f6',
      dark: '#1976d2',
      contrastText: '#000000'
    },
    secondary: { 
      main: '#8b5cf6',
      light: '#a78bfa',
      dark: '#7c3aed',
      contrastText: '#000000'
    },
    success: { 
      main: '#4caf50',
      light: '#66bb6a',
      dark: '#2e7d32',
      contrastText: '#000000'
    },
    warning: { 
      main: '#ff9800',
      light: '#ffb74d',
      dark: '#ed6c02',
      contrastText: '#000000'
    },
    error: { 
      main: '#ef5350',
      light: '#e57373',
      dark: '#d32f2f',
      contrastText: '#ffffff'
    },
    info: { 
      main: '#03a9f4',
      light: '#29b6f6',
      dark: '#0288d1',
      contrastText: '#000000'
    },
    background: { 
      default: '#121212', 
      paper: '#1e1e1e' 
    },
    text: {
      primary: '#ffffff',
      secondary: '#b0b0b0'
    },
    divider: '#333333'
  }
};

const baseThemeOptions: ThemeOptions = {
  shape: { 
    borderRadius: 8 
  },
  transitions: {
    duration: {
      shortest: 150,
      shorter: 200,
      short: 250,
      standard: 300,
      complex: 375,
      enteringScreen: 225,
      leavingScreen: 195,
    },
    easing: {
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
    },
  },
  typography: {
    fontFamily: `'Inter', system-ui, -apple-system, 'Segoe UI', 'Roboto', 'Arial', sans-serif`,
    h1: { 
      fontWeight: 700,
      fontSize: '1.875rem',
      lineHeight: 1.2
    },
    h2: { 
      fontWeight: 700,
      fontSize: '1.5rem',
      lineHeight: 1.3
    },
    h3: { 
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.3
    },
    h4: { 
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: 1.4
    },
    h5: { 
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.4
    },
    h6: { 
      fontWeight: 600,
      fontSize: '0.875rem',
      lineHeight: 1.5
    },
    body1: {
      fontSize: '0.875rem',
      lineHeight: 1.6
    },
    body2: {
      fontSize: '0.8rem',
      lineHeight: 1.6
    },
    button: { 
      textTransform: 'none', 
      fontWeight: 600,
      fontSize: '0.8rem'
    },
    caption: {
      fontSize: '0.7rem',
      lineHeight: 1.5
    },
    overline: {
      fontSize: '0.7rem',
      fontWeight: 700,
      letterSpacing: '0.1em',
      textTransform: 'uppercase'
    }
  },
  spacing: 8,
  components: {
    MuiCard: { 
      styleOverrides: { 
        root: { 
          boxShadow: '0 1px 6px rgba(0,0,0,0.04)',
          border: '1px solid #f1f5f9',
          borderRadius: '12px',
          transition: 'background-color 0.3s ease, color 0.3s ease, box-shadow 0.3s ease'
        } 
      } 
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '12px',
          '&:last-child': {
            paddingBottom: '12px'
          }
        }
      }
    },
    MuiChip: { 
      defaultProps: { 
        size: 'small' 
      },
      styleOverrides: {
        root: {
          fontWeight: 500
        }
      }
    },
    MuiButton: { 
      defaultProps: { 
        variant: 'contained' 
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 16px',
          transition: 'background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease'
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          transition: 'background-color 0.3s ease, color 0.3s ease'
        }
      }
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: '1px solid #e5e7eb'
        }
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          boxShadow: 'none'
        }
      }
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '2px 8px',
          transition: 'background-color 0.3s ease, color 0.3s ease',
          '&.Mui-selected': {
            backgroundColor: '#f1f5f9',
            '&:hover': {
              backgroundColor: '#e2e8f0'
            }
          }
        }
      }
    }
  }
};

export const createAppTheme = (mode: 'light' | 'dark') => {
  const themeOptions = mode === 'light' ? lightThemeOptions : darkThemeOptions;
  return createTheme({
    ...baseThemeOptions,
    ...themeOptions,
    palette: {
      ...baseThemeOptions.palette,
      ...themeOptions.palette
    }
  });
};

export default createAppTheme;
